import { Box, Paper, Typography } from '@mui/material';
import { useEffect, useState } from 'react';
import Layout from '../../../components/layout/Layout';
import { useNavigate } from 'react-router-dom';
import { useFindMigrationsLazy } from '@src/adapters/hooks/migration/useFindMigrations';
import MigrationDTO from '@src/adapters/dto/migration/MigrationDTO';
import List from './components/List';
import { Routes } from '@src/navigation/Routes';
import buildRouteWithQueryParams from '@src/navigation/RouteWithQueryParams';
import Filter from './components/Filter';
import { useForm, useWatch } from 'react-hook-form';
import { MigrationStatus } from '@src/constants/MigrationStatus';
import MigrationFilterDTO from '@src/adapters/dto/migration/MigrationFilterDTO';
import { useAuth } from '@src/auth/hooks/useAuth';

const availableStatuses = [
  { id: MigrationStatus.Pending, name: 'Pending', checked: false },
  { id: MigrationStatus.InProgress, name: 'In progress', checked: false },
  { id: MigrationStatus.Query, name: 'Query', checked: false },
  {
    id: MigrationStatus.InApproval,
    name: 'In Approval',
    checked: false,
  },
  { id: MigrationStatus.Complete, name: 'Complete', checked: false },
];

function Migration() {
  const navigate = useNavigate();
  const { execute: find } = useFindMigrationsLazy();
  const { currentHospitalId } = useAuth();
  const [procedureId, setProcedureId] = useState<string | null>('');
  const [consultantId, setConsultantId] = useState<string | null>('');
  const [currentPage, setCurrentPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [migrations, setMigrations] = useState<MigrationDTO[]>([]);
  const [hasInitialized, setHasInitialized] = useState(false);
  const [sortAsc, setSortAsc] = useState(false);
  const [sortKey, setSortKey] = useState('updated');

  const [recordCount, setRecordCount] = useState(0);
  const { control, setValue } = useForm({
    defaultValues: {
      statuses: availableStatuses.filter((status) => status.checked),
      specialityId: '',
      userId: '',
    },
  });

  const userId = useWatch({ control, name: 'userId' });
  const statuses = useWatch({ control, name: 'statuses' });
  const specialityId = useWatch({ control, name: 'specialityId' });

  useEffect(() => {
    const localFilter = localStorage.getItem('migrationFilter');
    if (localFilter && localFilter !== 'undefined') {
      const filter = JSON.parse(localFilter) as MigrationFilterDTO;

      if (filter.consultantId) {
        setConsultantId(filter.consultantId);
      }

      if (filter.currentPage !== undefined) {
        setCurrentPage(filter.currentPage);
      } else {
        setCurrentPage(0);
      }

      if (filter.pageSize) {
        setPageSize(filter.pageSize);
      }

      if (filter.procedureId) {
        setProcedureId(filter.procedureId);
      }

      setSortAsc(filter.sortAscending ?? false);
      setSortKey(filter.sortKey ?? 'updated');

      if (filter.specialityId) {
        setValue('specialityId', filter.specialityId);
      }

      if (filter.statuses) {
        setValue(
          'statuses',
          availableStatuses.filter((as) => filter.statuses!.includes(as.id))
        );
      }

      if (filter.userId) {
        setValue('userId', filter.userId);
      }
    } else {
      setCurrentPage(0);
      setPageSize(10);
      setSortAsc(false);
      setSortKey('updated');
    }

    setHasInitialized(true);
  }, [setValue]);

  useEffect(() => {
    if (!hasInitialized || !currentHospitalId) return;

    const statusIds = statuses?.map((s) => s.id) ?? [];

    const fullFilter: MigrationFilterDTO = {
      hospitalId: currentHospitalId,
      userId,
      statuses: statusIds,
      specialityId,
      consultantId: consultantId || undefined,
      procedureId: procedureId || undefined,
      currentPage,
      pageSize,
      sortKey,
      sortAscending: sortAsc,
    };

    localStorage.setItem('migrationFilter', JSON.stringify(fullFilter));

    find({ filter: fullFilter }).then((result) => {
      setMigrations(result.migrations);
      if (result.recordCount !== undefined) {
        setRecordCount(result.recordCount);
      }
    });
  }, [
    hasInitialized,
    userId,
    statuses,
    specialityId,
    consultantId,
    procedureId,
    currentPage,
    pageSize,
    sortKey,
    sortAsc,
    find,
    currentHospitalId,
  ]);

  const updateSelectedMigration = (migration: MigrationDTO) => {
    const url = buildRouteWithQueryParams(Routes.MigrationDetail, {
      id: migration.id,
      name: migration.originalFilename,
    });
    navigate(url);
  };

  const UpdateSortKey = (key: string) => {
    setSortAsc((prev) => (key === sortKey ? !prev : false));
    setSortKey(key);
  };

  return (
    <Layout>
      <Box
        sx={{
          padding: '0 20px 20px 20px',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          width: '100%',
        }}>
        <Typography variant="h4">Migration</Typography>
      </Box>
      <Paper
        elevation={8}
        sx={{
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'space-between',
        }}>
        <Filter
          control={control}
          procedureId={procedureId}
          setProcedureId={setProcedureId}
          consultantId={consultantId}
          setConsultantId={setConsultantId}
        />
        <List
          migrations={migrations}
          selectedPage={currentPage}
          rowsPerPage={pageSize}
          recordCount={recordCount}
          sort={{ asc: sortAsc, key: sortKey }}
          changePage={setCurrentPage}
          changeRowsPerPage={setPageSize}
          setSelectedMigration={updateSelectedMigration}
          setSortKey={UpdateSortKey}
        />
      </Paper>
    </Layout>
  );
}

export default Migration;
